import { HeightUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { ObstetricalCounters } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { BaseObstetricalFileForm } from "../BaseObstetricalFileForm";
import { ObstetricalFileMedicalSummary } from "../ObstetricalFileMedicalSummary";
import { ObstetricalFileVitalSign } from "../ObstetricalFileVitalSign";
import { QuebecObstetricalFileFormPageFour } from "./PageFour/QuebecObstetricalFileFormPageFour";
import { QuebecObstetricalFileFormPageOne } from "./PageOne/QuebecObstetricalFileFormPageOne";
import { QuebecObstetricalFileFormPageThree } from "./PageThree/QuebecObstetricalFileFormPageThree";
import { QuebecObstetricalFileFormPageTwo } from "./PageTwo/QuebecObstetricalFileFormPageTwo";

class QuebecObstetricalFileForm extends BaseObstetricalFileForm {
    public PageOne: QuebecObstetricalFileFormPageOne;
    public PageTwo: QuebecObstetricalFileFormPageTwo;
    public PageThree: QuebecObstetricalFileFormPageThree;
    public PageFour: QuebecObstetricalFileFormPageFour;
    public ObstetricalCounters: ObstetricalCounters;
    public WeightBeforePregnancyUnit: WeightUnit;
    public WeightBeforePregnancy: number;
    public PrePregnancyBodyMassIndex: number;

    constructor(fields?: Partial<QuebecObstetricalFileForm>) {
        super(fields);
        Object.assign(this, fields);

        this.PageOne = new QuebecObstetricalFileFormPageOne(fields?.PageOne);
        this.PageTwo = new QuebecObstetricalFileFormPageTwo(fields?.PageTwo);
        this.PageThree = new QuebecObstetricalFileFormPageThree(fields?.PageThree);
        this.PageFour = new QuebecObstetricalFileFormPageFour(fields?.PageFour);
        this.ObstetricalCounters = new ObstetricalCounters(fields?.ObstetricalCounters);
        this.WeightBeforePregnancyUnit = fields?.WeightBeforePregnancyUnit ?? WeightUnit.Kilogram;
    }

    public get WeightBeforePregnancyInKg(): number {
        if (this.WeightBeforePregnancyUnit === WeightUnit.Kilogram)
            return this.WeightBeforePregnancy;

        return WeightUnitConverter.poundToKilogram(this.WeightBeforePregnancy);
    }

    public setDefaultWeightUnit(unit: WeightUnit): void {
        this.PageTwo.WeightUnit = unit;
        this.setWeightBeforePregnancyUnit(unit);
        this.PageFour.setWeightUnit(unit);
        this.PageOne.PreviousPregnancies.setMassUnit(unit);
    }

    public setDefaultHeightUnit(unit: HeightUnit): void {
        this.PageTwo.HeightUnit = unit;
    }

    public setWeightBeforePregnancyUnit(unit: WeightUnit): void {
        if (this.WeightBeforePregnancyUnit === unit)
            return;

        this.WeightBeforePregnancy = WeightUnitConverter.convert(this.WeightBeforePregnancyUnit, unit, this.WeightBeforePregnancy, 2);
        this.WeightBeforePregnancyUnit = unit;
    }

    public setPrenatalVisitVitalSigns(obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary, consultationDate: Date, contributionCreationDate: Date, contributionId: string): void {
        const isVitalSignReused = this.isVitalSignReusedOnPageFour();
        this.PageFour.PrenatalVisits.forEach((visit, idx) => {
            const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                obstetricalFileMedicalSummary: obstetricalFileMedicalSummary,
                emrTemplateId: visit.VitalSignEmrTemplateId,
                consultationDate: consultationDate,
                obstetricalFileContributionDate: contributionCreationDate,
                skipLoadLastHour: this.shouldSkipLastHourVitalSign(idx, visit.VitalSignEmrTemplateId, obstetricalFileMedicalSummary, consultationDate) || visit.ContributionId !== contributionId,
                weightUnit: this.PageFour.WeightUnit
            });

            // if the vital sign selected was a reuse from PageTwo then updates the PageTwo vital sign too
            if (isVitalSignReused && idx === 0)
                this.PageTwo.VitalSignEmrTemplateId = vitalSign?.emrTemplateId;

            visit.VitalSign = vitalSign;
            visit.VitalSignEmrTemplateId = vitalSign?.emrTemplateId;
        });
    }

    public isVitalSignReusedOnPageFour(): boolean {
        return !!this.PageTwo.VitalSignEmrTemplateId
            && this.PageFour.PrenatalVisits?.any()
            && !!this.PageFour.PrenatalVisits[0].VitalSignEmrTemplateId
            && this.PageFour.PrenatalVisits[0].VitalSignEmrTemplateId === this.PageTwo.VitalSignEmrTemplateId;
    }

    private shouldSkipLastHourVitalSign(idx: number, emrTemplateId: string, obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary, consultationDate: Date): boolean {
        return (
            idx === 0 // if it is the first visit
            && !!this.PageTwo.VitalSignEmrTemplateId // if there is a vital sign selected on page two
            && (emrTemplateId == null || // if there is no vital sign selected for the current visit
                emrTemplateId != this.PageTwo.VitalSignEmrTemplateId || // if the vital sign selected for the current visit is not a reuse from page two
                obstetricalFileMedicalSummary.getVitalSignBy(emrTemplateId, consultationDate) !== null) // if the vital sign selected for the current visit is a reuse, but the vital sign doesn't exist anymore
        );
    }
}

export { QuebecObstetricalFileForm };
